import React, { useEffect, useState, useCallback, useContext } from 'react'
import { Box, Typography, Divider, Paper, Grid, Select, MenuItem, FormControl, Button, Card, CardContent, Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';

import { AppContext } from 'core/context/';
import { getDynamicPricingConfig, getDynamicPricingInfo, subscribeToDynamicPricing, unsubscribeFromDynamicPricing } from 'services/operations/dynamicPricing';
import ScrollDialog from 'components/ScrollDialog';
import AmountDialog from './AmountDialog';

const DynamicPricing = () => {

  const { enqueueSnackbar } = useSnackbar();
  const [libraryList, setLibrayList] = useState([]);
  const [librarySelected, setLibraySelected] = useState({});
  const [pricingInfo, setPricingInfo] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(false);
  const [openAmountModal, setOpenAmountModal] = useState(false);

  const { libraries } = useContext(AppContext);
  
  useEffect(() => {
    if(libraries?.length){
      setLibrayList(libraries);
      setLibraySelected(libraries[0]);
    }
  }, [libraries]);

  const getPricingConfig = useCallback((library) => {
    const data = {
      propertyId: library?.libraryId,
      propertyType: "LIBRARY",
    };

    getDynamicPricingConfig({
      ...data,
    }).then(({response, error}) => {
      if(response){
        setPricingInfo(false);
        setPricingConfig({...response});
      } else {
        setPricingConfig(false);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar]);

  const fetchDynamicPricingInfo = useCallback((library) => {
    const data = {
      propertyId: library?.libraryId,
      propertyType: "LIBRARY",
    };

    getDynamicPricingInfo({
      ...data,
    }).then(({response, error}) => {
      if(response){
        setPricingInfo({...response});
        if(response?.subscribed === true){
          getPricingConfig(library);
        }else{
          setPricingConfig(false);
        }
      } else {
        setPricingInfo(false);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar, getPricingConfig]);

  useEffect(() => {
    if(librarySelected?.libraryId){
      setPricingInfo(false);
      setPricingConfig(false);
      fetchDynamicPricingInfo(librarySelected);
    }
  }, [librarySelected, fetchDynamicPricingInfo]);

  function subscribeDynamicPricing() {
    const data = {
      propertyId: librarySelected?.libraryId,
      propertyType: "LIBRARY",
    };

    subscribeToDynamicPricing({
      ...data,
    }).then(({response, error}) => {
      if(response){
        getPricingConfig(librarySelected);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onUnsubscribeBtnClick = useCallback(() => {
    const data = {
      propertyId: librarySelected?.libraryId,
      propertyType: "LIBRARY",
    };

    unsubscribeFromDynamicPricing({
      ...data,
    }).then(({response, error}) => {
      if(response){
        getPricingConfig(librarySelected);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  },[librarySelected, enqueueSnackbar, getPricingConfig]);

  const onAmountUpdate = useCallback((success) => {
    setOpenAmountModal(false);
    if(success){
      getPricingConfig(librarySelected);
    }
  },[librarySelected, getPricingConfig]);

  return (
    <Paper sx={{ minWidth: 275, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container columnSpacing={4} rowSpacing={1} >
          <Grid item xs={12} sm={6} md={6} xl={4} lg={4}>
            <Typography component="h2" variant="h6" color="secondary"  gutterBottom sx={{mb: 1}}>
              Dynamic Pricing
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6} xl={8} lg={8}  sx={{display: 'flex'}} justifyContent="flex-end">
            <FormControl sx={{fontSize: 12}}>
              <Select 
                size="small"
                labelId="library-select-label"
                value={librarySelected}
                label="Library"
                variant="standard"
                onChange={(e) => setLibraySelected(e.target.value)}
                sx={{ padding: '4px 0 1px', fontSize: 14 }}
                disableUnderline
              >
                {libraryList.map((library, index) => <MenuItem key={index} value={library}>{library.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Divider />
        <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} sx={{mt: 2}} justifyContent="center">
          { pricingInfo && pricingInfo?.subscribed === false ? (
            <Grid item xs={10} sm={10} md={10} xl={10} lg={10}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {pricingInfo?.description}
                </Typography>
              </Box>
              <Box sx={{mt: 2, textAlign: 'center'}}>
                <Button variant="contained" onClick={subscribeDynamicPricing}>Subscribe</Button>
              </Box>
            </Grid>
          ): null}
          {pricingConfig ? (
            <Grid item xs={12} sm={12} md={6} xl={4} lg={4}>
              <Card sx={{ minWidth: 275 }} >
                <CardContent sx={{ textAlign: "center", p: '1rem', pb: '0px !important'}}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="column"  alignItems="flex-start">
                      <Typography component="p" display="block" variant="caption">Subscribed on</Typography>
                      <Typography component="p" display="block" variant="subtitle2">{pricingConfig?.createdOn}</Typography>
                    </Stack>
                    <Stack direction="column"  alignItems="flex-start">
                      <Typography component="p" display="block" variant="caption">Expires on</Typography>
                      <Typography component="p" display="block" variant="subtitle2">{pricingConfig?.expiresOn}</Typography>
                    </Stack>
                  </Stack>
                  <br />
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="column"  alignItems="flex-start">
                      <Typography component="p" display="block" variant="caption">Current Price</Typography>
                      <Button
                        variant="text"
                        color="error"
                        sx={{p: 0, fontSize: '1.1rem'}}
                        onClick={() => setOpenAmountModal(true)}
                        endIcon={<EditIcon />}
                      >
                        {pricingConfig?.value}
                      </Button>
                      <Typography component="p" display="block" variant="caption">Last modified on {pricingConfig?.modifiedOn}</Typography>
                    </Stack>
                    <Stack direction="column"  alignItems="flex-start" sx={{mt: 1}}>
                      <Typography component="p" display="block" variant="caption">&nbsp;</Typography>
                      <Typography component="p" display="block" color={pricingConfig?.enabled ? 'green':'red'} variant="subtitle2">{pricingConfig?.enabled ? 'Active':'Disabled'}</Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row"  justifyContent="flex-end" sx={{mt: 1}}>
                    <Button variant="text" color="info" onClick={onUnsubscribeBtnClick}>Unsubscribe</Button>
                  </Stack>
                </CardContent>
              </Card>
              <ScrollDialog
                title="Add Money"
                onModalClose={() => setOpenAmountModal(false)}
                open={openAmountModal}
                showCloseIcon={true}
                maxWidth="xs"
                disableDialogActions={true}
              >
                <AmountDialog onAmountUpdate={onAmountUpdate} library={librarySelected} />
              </ScrollDialog>
            </Grid>
          ): null}
        </Grid>
      </Box>
    </Paper>
  );
}
 
export default DynamicPricing;
